import React, { Fragment, useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  ListGroup,
  ListGroupItem,
  Collapse,
  Progress,
  ButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from "reactstrap";
import _ from "lodash";
import Swal from "sweetalert2";

import MatchIndicator   from "../commons/MatchIndicator";
import ApplicantData     from "../applicant/Data";
import ApplicantStages   from "../applicant/Stages";

import {
  switchPostulationState,
  getCv,
  submitSingleNextStage,
  createCourseSelections
} from "./Axios";

const ApplicantsHit = ({
                         hit,
                         isPostulation,
                         courseId,
                         workFlow,
                         showNotification,
                         handleCheckApplicant,
                         isChecked,
                         reloadQuery,
                         isAdmin,
                         updateHit,
                         reloadAfterUpdate,
                         selectionMap = {},
                         boardRef,
                         currentUserRole
                       }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [collapseAnswers, setCollapseAnswers] = useState(
    isPostulation && hit._source.postulation_answers?.length >= 1
  );
  const [applicantHit, setApplicantHit] = useState(hit._source);

  useEffect(() => {
    const incoming = hit._source;
    if (
      !incoming.updated_at ||
      !applicantHit.updated_at ||
      new Date(incoming.updated_at) > new Date(applicantHit.updated_at)
    ) {
      setApplicantHit(incoming);
    }
  }, [hit._source.updated_at]);

  const toggleDropdown  = () => setDropdownOpen(o => !o);
  const toggleAnswers   = () => setCollapseAnswers(o => !o);

  const handleAddToBoard = () => {
    Swal.showLoading();

    createCourseSelections(courseId, [applicantHit.id], async resp => {
      Swal.close();

      if (resp.status === 200) {
        await boardRef.current?.fetchSelections();
        reloadQuery?.();
        Swal.fire("¡Agregado!", "El candidato fue agregado al tablero de seleccionados.", "success");
      } else {
        const msg = resp.data?.errors?.join(", ") || "No se pudo procesar la solicitud.";
        Swal.fire("Error", msg, "error");
      }
    });
  };

  const handleSwitchPostulationState = switchState => {
    const map = {
      approve: { txt: "Aceptar",   ok: "Aceptada"  },
      reject : { txt: "Rechazar",  ok: "Rechazada" }
    };
    const { txt, ok } = map[switchState] || {};

    Swal.fire({
      icon: "question",
      title: "¿Estás seguro?",
      text: `La postulación se va a ${txt}`,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Sí, ${txt}`,
      cancelButtonText: "Cancelar"
    }).then(result => {
      if (!result.value) return;

      const fd = new FormData();
      fd.append("switch_state", switchState);

      switchPostulationState(courseId, applicantHit.id, fd, resp => {
        if (resp.status === 200) {
          setApplicantHit(resp.data);
          updateHit(resp.data);
          reloadAfterUpdate?.();
          Swal.fire("¡Hecho!", `La postulación ha sido ${ok}.`, "success");
        } else {
          Swal.fire("Error", "No se pudo procesar la solicitud.", "error");
        }
      });
    });
  };

  const handleSubmitNextStage = async () => {
    const applicant = isPostulation ? applicantHit.applicant : applicantHit;

    if (!applicant.enrollment || !Array.isArray(applicant.enrollment.stages)) {
      applicant.enrollment = { stages: [] };
    }

    if (!Array.isArray(workFlow) || workFlow.length === 0) {
      Swal.fire("Error", "El flujo de trabajo no está definido. Contacta a soporte.");
      return;
    }

    const firstRequired     = workFlow.find(s => s.required) || { id: 0 };
    const completedTypes    = applicant.enrollment.stages.map(s => s.stage_type) || [];
    const completedRequired = workFlow
      .filter(s => s.required && completedTypes.includes(s.type))
      .map(s => s.id);

    const currentIdx = completedRequired.length
      ? Math.max(...completedRequired)
      : firstRequired.id;

    const requiredPending = workFlow
      .filter(s => s.required && s.id > currentIdx)
      .map(s => s.id);

    const stageOpts = workFlow.reduce((acc, s) => {
      const reqLabel     = s.required ? " (requerida)" : "";
      const firstSelect  = applicant.enrollment.stages.length === 0;
      const disabled     = firstSelect
        ? s.id > firstRequired.id
        : requiredPending.some(id => id < (s.id || 0));

      acc[s.type] = { label: `${s.name}${reqLabel}`, disabled };
      return acc;
    }, {});

    const { value: stageType } = await Swal.fire({
      title: "Siguiente Etapa",
      input: "select",
      inputOptions: Object.fromEntries(
        Object.entries(stageOpts).map(([k, v]) => [k, v.label])
      ),
      inputPlaceholder: "Seleccione una Etapa",
      showCancelButton: true,
      confirmButtonText: "Sí, avanzar",
      inputValidator: val => {
        if (!val) return "Debes seleccionar una etapa.";
        if (stageOpts[val].disabled) return "No puedes saltarte una etapa requerida.";
        return null;
      }
    });

    if (!stageType) return;

    Swal.showLoading();
    const fd = new FormData();
    fd.append("next_stage", stageType);

    submitSingleNextStage(courseId, applicantHit.id, fd, resp => {
      Swal.close();

      if (resp.status === 200) {
        setApplicantHit(resp.data);
        updateHit(resp.data);
        reloadAfterUpdate?.();
        Swal.fire("¡Genial!", "Candidato cambiado de etapa.", "success");
      } else {
        const err = resp.status === 422
          ? _.flatMap(resp.data, v => v)[0]
          : "No autorizado.";
        Swal.fire("Error", err, "error");
      }
    });
  };

  const applicant    = isPostulation ? applicantHit.applicant : applicantHit;
  const { user }     = applicant;
  const selType      = isPostulation
    ? selectionMap[applicantHit.postulation_id || applicantHit.id]
    : undefined;
  const hasRejectedStage      = applicant.enrollment?.stages?.some(st => st.aasm_state === "rejected");
  const isRejectedPostulation = applicantHit.aasm_state === "rejected";
  const canAddToBoard         = !selType && !isRejectedPostulation && !hasRejectedStage;

  const profileLink  = `/applicants/${applicant.id}${courseId ? `?course_id=${courseId}` : ""}`;

  useEffect(() => {
    if (!isPostulation) return;
    const postulationRejected = applicantHit.aasm_state === "rejected";
    const stageRejected       = applicantHit.enrollment?.stages?.some(
      st => st.aasm_state === "rejected"
    );

    if ((postulationRejected || stageRejected) && selType) {
      boardRef.current?.fetchSelections();
    }
  }, [
    applicantHit.aasm_state,
    applicantHit.enrollment?.stages,
    selType,
    isPostulation,
    boardRef
  ]);

  const postId = hit._source.postulation_id || hit._source.id;

  return (
    <Card className="mb-3 publication-list-item item-card material-shadow" id={`list-card-${postId}`}>
      <CardBody>
        <div className="d-flex justify-content-between flex-wrap align-items-lg-center">
          {/* --- datos y check --- */}
          <div className="col-lg-5 flex-grow-1 pe-md-3 p-0 d-flex align-items-start">
            <input
              type="checkbox"
              value={hit._source.id}
              onChange={handleCheckApplicant}
              checked={isChecked}
              className="float-start me-2"
            />
            <div style={{ flex: 1 }}>
              <ApplicantData user={user} applicant={applicantHit} />

              {isPostulation && applicantHit.sent_to_head && (
                <span className="badge bg-warning text-dark ms-2">
                  <i className="fas fa-paper-plane me-1" /> Enviado a jefatura
                </span>
              )}

              {isPostulation && selType && (
                <span className={`badge ms-2 ${selType === "selected" ? "bg-success" : "bg-secondary"}`}>
                  {selType === "selected" ? "Seleccionado" : "Reserva"}
                </span>
              )}
            </div>
          </div>

          {isPostulation ? (
            <>
              <div className="col-lg-2 p-0 flex-fill text-center d-flex flex-column align-items-center justify-content-center">
                <MatchIndicator matchpercentage={Math.round(applicantHit.cv_score || 0)} />
              </div>
              <div className="col-lg-3 flex-fill p-0">
                <ApplicantStages
                  courseId={courseId}
                  postulationId={applicantHit.id}
                  applicant={applicant}
                  postulatedAt={applicantHit.created_at}
                  postulationState={applicantHit.aasm_state}
                  stages={applicant.enrollment ? applicant.enrollment.stages : []}
                  setApplicanttHit={setApplicantHit}
                  updateHit={updateHit}
                  boardRef={boardRef}
                  workFlow={workFlow}
                  showNotification={showNotification}
                  reloadQuery={reloadQuery}
                  isAdmin={isAdmin}
                  currentUserRole={currentUserRole}
                />
              </div>
            </>
          ) : (
            <div className="col-lg-4 flex-fill p-0">
              <p className="small text-primary mb-2 mt-2">
                Tiene el {parseInt(user.profile_progress, 10).toFixed()}% del Perfil Cargado
              </p>
              <Progress animated value={parseInt(user.profile_progress, 10).toFixed()}>
                {parseInt(user.profile_progress, 10).toFixed()}%
              </Progress>
            </div>
          )}

          <div className="col-lg-2 p-0 d-flex flex-column align-items-center justify-content-center">
            {!isPostulation && (
              <Button outline size="sm" color="info" className="m-2" href={profileLink}>
                Ver Perfil
              </Button>
            )}

            {isPostulation && (Array.isArray(currentUserRole)
              ? !currentUserRole.includes("job_head")
              : currentUserRole !== "job_head") && (
              <ButtonDropdown isOpen={dropdownOpen} toggle={toggleDropdown} size="sm" className="m-2">
                <DropdownToggle caret color="secondary">
                  <i className="fas fa-user-cog me-2" /> Gestionar
                </DropdownToggle>

                <DropdownMenu>
                  {canAddToBoard && (
                    <>
                      <DropdownItem onClick={handleAddToBoard}>
                        Agregar a Seleccionados / Reserva
                      </DropdownItem>
                      <DropdownItem divider />
                    </>
                  )}

                  {applicantHit.aasm_state === "pending" && (
                    <>
                      <DropdownItem onClick={() => handleSwitchPostulationState("approve")}>
                        Aceptar Postulación
                      </DropdownItem>
                      <DropdownItem onClick={() => handleSwitchPostulationState("reject")}>
                        Rechazar Postulación
                      </DropdownItem>
                      <DropdownItem divider />
                    </>
                  )}

                  {applicantHit.aasm_state === "approved" &&
                    !applicant.enrollment?.stages?.some(st => st.aasm_state === "rejected") && (
                      <DropdownItem onClick={handleSubmitNextStage}>
                        Avanzar Etapa
                      </DropdownItem>
                    )}

                  <DropdownItem href={profileLink}>Ver Perfil</DropdownItem>
                </DropdownMenu>
              </ButtonDropdown>
            )}

            {isPostulation && (Array.isArray(currentUserRole)
              ? currentUserRole.includes("job_head")
              : currentUserRole === "job_head") && (
              <Button
                outline
                size="sm"
                color="info"
                className="m-2"
                href={profileLink}
              >
                Ver Perfil
              </Button>
            )}
          </div>
        </div>
      </CardBody>

      {isPostulation && applicantHit.postulation_answers?.length >= 1 && (
        <Collapse isOpen={collapseAnswers}>
          <CardFooter className="bg-light p-0">
            <ListGroup flush>
              {applicantHit.postulation_answers.map((ans, i) => (
                <ListGroupItem key={i} className="bg-light">
                  <span className="fw-bold d-block lh-sm">{ans.publication_question_name}</span>
                  <em className="text-muted lh-sm">
                    {ans.answer === true || ans.answer === "true"
                      ? "Sí"
                      : ans.answer === false || ans.answer === "false"
                        ? "No"
                        : ans.answer || "Sin respuesta"}
                  </em>
                </ListGroupItem>
              ))}
            </ListGroup>
          </CardFooter>
        </Collapse>
      )}
    </Card>
  );
};

export default ApplicantsHit;
